import type { ModelRegistryEntry, ModelPricingMap } from '../types';

/**
 * Per-word, per-stage cost math (2026-07-05).
 *
 * The worker keeps a raw token ledger per word (KV `wordcost:<id>`, served by
 * GET /api/words/:id/costs) bucketed by the provider-reported modelId. Dollars
 * are computed HERE with the same registry pricing map Settings and the bench
 * use (OpenRouter list + manual fallback) — pricing is keyed by registry id,
 * so each modelId bucket is mapped back to its registry entry first.
 */

export interface StageCostBucket {
  calls: number;
  prompt_tokens: number;
  completion_tokens: number;
}

export interface StageCostEntry extends StageCostBucket {
  byModel: Record<string, StageCostBucket>;
  lastModelId?: string;
  lastTs?: string;
}

export interface WordCostsDoc {
  stages: Record<string, StageCostEntry>;
  updatedAt?: string;
}

export interface StageCost {
  usd: number;
  calls: number;
  /** true when one or more model buckets had no known price (usd is a floor) */
  partial: boolean;
}

// Provider-reported model ids can carry date suffixes the registry omits
// (e.g. "claude-sonnet-4-6-20260115" vs registry "claude-sonnet-4-6").
function registryIdForModelId(modelId: string, registry: ModelRegistryEntry[]): string | null {
  const exact = registry.find((m) => m.modelId === modelId);
  if (exact?.id) return exact.id;
  const prefix = registry.find((m) => modelId.startsWith(m.modelId) || m.modelId.startsWith(modelId));
  return prefix?.id || null;
}

export function stageCost(
  entry: StageCostEntry | undefined,
  registry: ModelRegistryEntry[],
  pricing: ModelPricingMap
): StageCost | null {
  if (!entry || entry.calls === 0) return null;
  let usd = 0;
  let partial = false;
  for (const [modelId, bucket] of Object.entries(entry.byModel || {})) {
    const regId = registryIdForModelId(modelId, registry);
    const p = regId ? pricing[regId] : undefined;
    if (!p || p.input == null || p.output == null) {
      partial = true;
      continue;
    }
    usd += (bucket.prompt_tokens / 1_000_000) * p.input + (bucket.completion_tokens / 1_000_000) * p.output;
  }
  return { usd, calls: entry.calls, partial };
}

export function wordTotalCost(
  doc: WordCostsDoc | null,
  registry: ModelRegistryEntry[],
  pricing: ModelPricingMap
): StageCost | null {
  if (!doc || !doc.stages) return null;
  let usd = 0;
  let calls = 0;
  let partial = false;
  let any = false;
  for (const entry of Object.values(doc.stages)) {
    const c = stageCost(entry, registry, pricing);
    if (!c) continue;
    any = true;
    usd += c.usd;
    calls += c.calls;
    partial = partial || c.partial;
  }
  return any ? { usd, calls, partial } : null;
}

export function fmtUsd(n: number): string {
  if (n === 0) return '$0';
  return n < 0.01 ? `$${n.toFixed(5)}` : `$${n.toFixed(4)}`;
}
